import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMe } from '../hooks/useMe';
import { useSidebar } from '../components/AppShell';
import { UsersTab } from '../components/UsersTab';
import { CompaniesTab } from '../components/CompaniesTab';
import { UserTeamsTab } from '../components/UserTeamsTab';

type Tab = 'users' | 'companies' | 'teams';

/**
 * Super-admin-only Settings page (ADR 0009 §4, §5). Houses three tabs:
 *   - Users: invite, edit, disable/re-enable
 *   - Companies: access-group partitions (who can see what)
 *   - Teams: named user groupings for filtering (no access impact)
 *
 * Teams are orthogonal to Companies. Creating a team doesn't grant or
 * revoke task visibility; it only adds an entry to the ViewerToggle.
 *
 * Guard is duplicated here as a direct-nav safeguard — the sidebar link
 * already hides for non-super-admins, but someone could paste `/settings`
 * into the URL. `me === null` means /me hasn't resolved yet → spinner.
 */
export function SettingsView() {
  const { me } = useMe();
  const [tab, setTab] = useState<Tab>('users');
  const { toggle } = useSidebar();

  if (me === null) {
    return (
      <div className="settings-view">
        <div className="settings-loading muted">Loading…</div>
      </div>
    );
  }
  if (!me.isSuperAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="scope-view settings-view">
      <header className="scope-view-header settings-header">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={toggle}
          aria-label="Open menu"
          title="Menu"
        >
          <span aria-hidden="true">☰</span>
        </button>
        <h1>Settings</h1>
        <div className="view-header-tools">
          <nav className="settings-tabs" role="tablist" aria-label="Settings sections">
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'users'}
              className={`settings-tab ${tab === 'users' ? 'is-active' : ''}`}
              onClick={() => setTab('users')}
            >
              Users
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'companies'}
              className={`settings-tab ${tab === 'companies' ? 'is-active' : ''}`}
              onClick={() => setTab('companies')}
            >
              Companies
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={tab === 'teams'}
              className={`settings-tab ${tab === 'teams' ? 'is-active' : ''}`}
              onClick={() => setTab('teams')}
            >
              Teams
            </button>
          </nav>
        </div>
      </header>
      <div className="settings-body">
        {tab === 'users' && <UsersTab />}
        {tab === 'companies' && <CompaniesTab />}
        {tab === 'teams' && <UserTeamsTab />}
      </div>
    </div>
  );
}
